import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ScheduledPostTarget } from 'types'
import { ScheduledPostTargetsClient, CreateScheduledPostTargetParams } from 'api/clients/ScheduledPostTargetsClient'
import { scheduledPostTargetsKeys } from './scheduledPostTargetsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { CheckBroken } from 'blaze-ui/icons'
import { uniqBy } from 'lodash'
import { filesKeys } from '../files/filesKeys'

export type Data = ScheduledPostTarget[]

export interface Variables extends CreateScheduledPostTargetParams {
  scheduled_post_id: number
  integration_name?: string
}

interface Context {
  previousTargets: ScheduledPostTarget[] | undefined
}

export const createScheduledPostTarget = async (variables: Variables, workspaceId: number) => {
  const response = await ScheduledPostTargetsClient.create({
    workspaceId,
    scheduledPostId: variables.scheduled_post_id,
    params: variables,
  })
  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Unexpected error response')
  }
  const responseData = response.data
  return 'scheduled_post_target' in responseData
    ? [responseData.scheduled_post_target]
    : responseData.scheduled_post_targets
}

export const useCreateScheduledPostTarget = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, unknown, Variables, Context>(
    'createScheduledPostTarget',
    async (variables) => {
      return createScheduledPostTarget(variables, workspaceId)
    },
    {
      onMutate: async (newTarget) => {
        queryClient.invalidateQueries(['schedules'])
        queryClient.invalidateQueries(['workspaces', workspaceId, 'schedules'])
        queryClient.invalidateQueries(filesKeys.all(workspaceId))
        await queryClient.cancelQueries(scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id))

        const previousTargets = queryClient.getQueryData<ScheduledPostTarget[]>(
          scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id),
        )

        const optimisticTarget: ScheduledPostTarget = {
          id: -1,
          status: 'pending',
          target_overrides: newTarget.target_overrides || {},
          scheduled_post_id: newTarget.scheduled_post_id,
          external_integration_id: newTarget.external_integration_id || 0, // Will be set by server response
          provider: newTarget.provider,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }

        queryClient.setQueryData(
          scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id),
          (old: ScheduledPostTarget[] = []) => uniqBy([...old, optimisticTarget], 'provider'),
        )

        const newIntegrationText = newTarget.integration_name ? ` - ${newTarget.integration_name}` : ''
        showToastMessage(
          `Posting to ${ProviderToName[newTarget.provider]}${newIntegrationText} enabled!`,
          'designRating',
          undefined,
          undefined,
          <CheckBroken />,
          'dark',
        )

        return { previousTargets }
      },
      onError: (err, newTarget, context) => {
        if (context?.previousTargets !== undefined) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.list(workspaceId, newTarget.scheduled_post_id),
            context.previousTargets,
          )
        }
      },
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries(scheduledPostTargetsKeys.list(workspaceId, variables.scheduled_post_id))
      },
    },
  )
}
